import React, { useState } from "react";
import Navigation from "../components/Navigation";
import {
  CheckCircle,
  ChevronUp,
  ChevronDown,
  ShieldCheck,
  Sparkles,
  MailCheck,
  Star,
  ArrowRight,
  Check,
  X,
  Quote,
  CreditCard,
  MessageSquareWarning,
  Send,
  AlertCircle,
} from "lucide-react";
import Footer from "../components/Footer";

const faqData = [
  {
    question: "Does RevUShield post responses automatically?",
    answer:
      "By default, responses can require approval before posting. You stay in control and can approve, edit, or reject suggested responses.",
  },
  {
    question: "Do I need to use the dashboard?",
    answer:
      "No. The main workflow can happen through email approvals, but the dashboard gives you visibility into reviews, responses, analytics, and settings.",
  },
  {
    question: "Does RevUShield connect to Google Business Profile?",
    answer:
      "Yes. RevUShield is designed to connect with Google Business Profile so reviews can be synced and responses can be managed.",
  },
  {
    question: "Can I edit AI-generated responses?",
    answer:
      "Yes. Every suggested response can be edited before approval, so your business voice stays accurate and personal.",
  },
  {
    question: "Is this meant for small businesses?",
    answer:
      "Yes. RevUShield is built for local businesses that want consistent review responses without adding more work to their day.",
  },
  {
    question: "Can I cancel anytime?",
    answer:
      "Yes. You can cancel your subscription from billing settings or by contacting support.",
  },
];

export default function LandingPage() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleIndex = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <Navigation />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-blue-950 to-indigo-950 text-white pt-36 pb-24 min-h-screen">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(99,102,241,0.35),transparent_35%),radial-gradient(circle_at_bottom_left,rgba(59,130,246,0.25),transparent_35%)]" />

        <div className="relative max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-4 py-2 text-sm text-indigo-100 mb-6">
                <Sparkles size={16} />
                Review management for local businesses
              </div>

              <h1 className="text-3xl md:text-5xl font-bold tracking-tight leading-tight">
                Respond to Google reviews in seconds.
                <span className="block text-indigo-300">
                  Right from your inbox.
                </span>
              </h1>

              <p className="text-lg text-slate-300 mt-6 leading-relaxed">
                RevUShield monitors your Google reviews, drafts professional
                responses, and lets you approve them with one click from email
                or dashboard.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <a
                  href="/register"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 font-medium text-white hover:bg-indigo-700 transition shadow-lg shadow-indigo-600/20"
                >
                  Start Free Trial
                  <ArrowRight size={18} />
                </a>

                <a
                  href="/email-preview"
                  className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/10 px-5 py-3 font-medium text-white hover:bg-white/15 transition"
                >
                  Preview Workflow
                </a>
              </div>

              <p className="text-sm text-slate-400 mt-5">
                Approve responses from email in seconds, or manage everything
                from your dashboard.
              </p>
            </div>

            {/* Email Approval Preview */}
            <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/15 shadow-2xl p-4 sm:p-5">
              {/* Fake email header */}
              <div className="flex items-center justify-between border-b border-white/10 pb-4 mb-4">
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-wide text-indigo-200 font-semibold">
                    RevUShield
                  </p>

                  <h3 className="text-white font-semibold mt-1 truncate">
                    New review needs approval
                  </h3>
                </div>

                <div className="bg-white/10 border border-white/10 rounded-full px-3 py-1 text-xs text-indigo-100">
                  Email Preview
                </div>
              </div>

              {/* Email body */}
              <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                {/* Review */}
                <div className="p-5 sm:p-6 space-y-5">
                  <div className="flex gap-3">
                    <div className="h-11 w-11 rounded-full bg-slate-200 flex items-center justify-center font-semibold text-slate-700 shrink-0">
                      SM
                    </div>

                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <h3 className="font-semibold text-slate-900">
                          Sarah Mitchell
                        </h3>

                        <span className="hidden sm:inline text-xs text-slate-400">
                          •
                        </span>

                        <span className="text-sm text-slate-500">
                          2 days ago
                        </span>
                      </div>

                      <div className="flex gap-0.5 mt-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            size={16}
                            className="fill-yellow-400 text-yellow-400"
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
                    “Absolutely love this place! The staff was friendly, the
                    service was fast, and everything exceeded my expectations.”
                  </p>

                  {/* Suggested response */}
                  <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4 space-y-3">
                    <div className="flex items-center gap-2 text-indigo-700 font-semibold text-sm">
                      <Sparkles size={16} />
                      Suggested Response
                    </div>

                    <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
                      Thank you so much, Sarah! We’re thrilled to hear you had
                      such a great experience with our team. We appreciate your
                      support and hope to see you again soon!
                    </p>

                    <div className="inline-flex items-center gap-2 rounded-full bg-white border border-indigo-100 px-3 py-1 text-xs text-indigo-700">
                      Tone: Professional
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
                    <button className="justify-center bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2 transition">
                      <Check size={16} />
                      Approve
                    </button>

                    <button className="border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-medium transition">
                      Edit Response
                    </button>

                    <button className="justify-center border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2 transition">
                      <X size={16} />
                      Reject
                    </button>
                  </div>

                  <div className="rounded-xl bg-slate-50 border border-slate-100 px-4 py-3">
                    <p className="text-xs text-slate-500 leading-relaxed">
                      Responses are never posted automatically without approval.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Review preview */}
            {/* <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/15 shadow-2xl p-5">
              <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4 sm:p-5 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex gap-3 min-w-0">
                    <div className="h-10 w-10 sm:h-11 sm:w-11 rounded-full bg-slate-200 flex items-center justify-center font-semibold text-slate-700 shrink-0 text-sm">
                      SM
                    </div>

                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <h3 className="font-semibold text-slate-900 truncate">
                          Sarah Mitchell
                        </h3>

                        <span className="hidden sm:inline text-xs text-slate-400">
                          •
                        </span>

                        <span className="text-sm text-slate-500">
                          2 days ago
                        </span>
                      </div>

                      <div className="flex gap-0.5 mt-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            size={16}
                            className="fill-yellow-400 text-yellow-400"
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  <span className="w-fit bg-orange-100 text-orange-700 text-xs font-medium px-3 py-1 rounded-full">
                    Needs Response
                  </span>
                </div>

                <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
                  “Absolutely love this place! The staff was friendly, the
                  service was fast, and everything exceeded my expectations.”
                </p>

                <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4 space-y-3">
                  <div className="flex items-center gap-2 text-indigo-700 font-semibold text-sm">
                    <Sparkles size={16} />
                    Suggested Response
                  </div>

                  <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
                    Thank you so much, Sarah! We’re thrilled to hear you had
                    such a great experience with our team. We appreciate your
                    support and hope to see you again soon!
                  </p>

                  <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-2 pt-1">
                    <button className="w-full sm:w-auto justify-center bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2 transition">
                      <Check size={16} />
                      Approve
                    </button>

                    <button className="w-full sm:w-auto border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-medium transition">
                      Edit
                    </button>

                    <button className="w-full sm:w-auto justify-center border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2 transition">
                      <X size={16} />
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            </div> */}
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="py-24 bg-slate-50 overflow-hidden">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 rounded-full bg-red-50 text-red-700 px-4 py-2 text-sm font-medium mb-5">
                <MessageSquareWarning size={16} />
                Review responses are often neglected
              </div>

              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                Most businesses know they should respond to reviews — but fall
                behind.
              </h2>

              <p className="text-slate-600 mt-4 text-lg max-w-2xl mx-auto">
                Reviews directly impact trust, local SEO, and customer
                decisions, but responding consistently is easy to forget during
                busy days and packed schedules.
              </p>

              <div className="mt-8 space-y-4 max-w-xl mx-auto lg:mx-0 lg:max-w-none">
                {[
                  "Reviews arrive during busy hours",
                  "Responses get delayed or forgotten",
                  "Negative reviews sit unanswered",
                  "Maintaining consistency takes time",
                ].map((item) => (
                  <div
                    key={item}
                    className="flex items-start gap-3 rounded-2xl bg-white border border-slate-200 p-4 shadow-sm text-left max-w-md mx-auto lg:mx-0"
                  >
                    <div className="mt-0.5 h-6 w-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center shrink-0">
                      <X size={14} />
                    </div>

                    <p className="text-slate-700 font-medium leading-relaxed">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Visual */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-red-100/40 to-indigo-100/30 blur-3xl rounded-full" />

              <div className="relative rounded-3xl border border-slate-200 bg-white shadow-xl overflow-hidden">
                <div className="bg-slate-100 border-b border-slate-200 px-5 py-4 flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-slate-900">
                      Google Reviews
                    </p>

                    <p className="text-xs text-slate-500 mt-0.5">
                      Unmanaged example
                    </p>
                  </div>

                  <span className="text-xs font-medium bg-red-100 text-red-700 px-3 py-1 rounded-full">
                    Attention Needed
                  </span>
                </div>

                <div className="p-5 space-y-4 bg-slate-50">
                  {[
                    {
                      name: "Sarah M.",
                      rating: 2,
                      text: "Appointment started late and nobody followed up afterward.",
                      age: "6 days ago",
                      unanswered: true,
                    },
                    {
                      name: "James R.",
                      rating: 3,
                      text: "Good experience overall but communication could've been better.",
                      age: "11 days ago",
                      unanswered: true,
                    },
                    {
                      name: "Melissa T.",
                      rating: 5,
                      text: "Loved the staff and atmosphere!",
                      age: "14 days ago",
                      unanswered: false,
                    },
                  ].map((review) => (
                    <div
                      key={review.name}
                      className="rounded-2xl border border-slate-200 bg-white p-4"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="font-semibold text-sm text-slate-900">
                            {review.name}
                          </p>

                          <div className="flex gap-0.5 mt-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                size={13}
                                className={
                                  star <= review.rating
                                    ? "fill-yellow-400 text-yellow-400"
                                    : "text-slate-300"
                                }
                              />
                            ))}
                          </div>
                        </div>

                        <span className="text-xs text-slate-400 whitespace-nowrap">
                          {review.age}
                        </span>
                      </div>

                      <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                        {review.text}
                      </p>

                      <div className="mt-4 flex items-center justify-between">
                        {review.unanswered ? (
                          <>
                            <span className="text-xs font-medium text-red-600">
                              No response yet
                            </span>

                            <div className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                          </>
                        ) : (
                          <span className="text-xs font-medium text-green-600">
                            Responded
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="howitworks" className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 text-center mb-16">
          <div className="inline-flex items-center gap-2 rounded-full border border-indigo-100 bg-indigo-50 px-4 py-2 text-sm font-medium text-indigo-700 mb-5">
            <Sparkles size={16} />
            Simple approval workflow
          </div>

          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
            A simple workflow that keeps your reputation active.
          </h2>

          <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
            RevUShield monitors your Google reviews, drafts thoughtful
            responses, and keeps you in control before anything gets posted.
          </p>
        </div>

        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            <StepCard
              step="1"
              icon={<ShieldCheck size={22} />}
              title="Connect Google Business"
              description="Securely connect your Google Business Profile and start syncing reviews in minutes."
            />

            <StepCard
              step="2"
              icon={<Sparkles size={22} />}
              title="Responses are prepared instantly"
              description="RevUShield creates professional, on-brand responses tailored to each review."
            />

            <StepCard
              step="3"
              icon={<MailCheck size={22} />}
              title="Approve from email"
              description="Approve, edit, or reject responses from your inbox or dashboard before posting."
            />

            <StepCard
              step="4"
              icon={<Send size={22} />}
              title="We post it for you"
              description="Approved responses are posted back to Google so your reputation stays active."
            />
          </div>

          <div className="mt-14 rounded-3xl border border-slate-200 bg-slate-50 p-6 md:p-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
              <div className="lg:col-span-2">
                <h3 className="text-xl font-bold text-slate-900">
                  Human-approved. Never automatic without permission.
                </h3>

                <p className="text-slate-600 mt-3 leading-relaxed">
                  RevUShield gives you the speed of automation while keeping you
                  in control. Every response can be reviewed before it goes
                  live.
                </p>
              </div>

              <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-green-50 text-green-600 flex items-center justify-center">
                    <Check size={20} />
                  </div>

                  <div>
                    <p className="font-semibold text-slate-900">
                      Approval required
                    </p>
                    <p className="text-sm text-slate-500">
                      Nothing posts until you say so.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      {/* <section id="features" className="py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              Built for busy local businesses
            </h2>

            <p className="text-slate-600 max-w-2xl mx-auto mt-4">
              Everything you need to stay on top of Google reviews without
              adding more work to your day.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FeatureCard
              icon={<Clock size={22} />}
              title="Save time every week"
              description="Eliminate manual writing for every review while keeping quality high."
            />

            <FeatureCard
              icon={<BellIcon size={22} />}
              title="Never miss a review"
              description="Get notified when new reviews come in and need attention."
            />

            <FeatureCard
              icon={<CheckCircle size={22} />}
              title="Stay consistent"
              description="Every response follows your preferred tone, style, and approval rules."
            />

            <FeatureCard
              icon={<Users size={22} />}
              title="Build customer trust"
              description="Active review responses make your business look engaged and reliable."
            />

            <FeatureCard
              icon={<ShieldCheck size={22} />}
              title="Stay in control"
              description="You approve or edit responses before they are posted."
              highlighted
            />

            <FeatureCard
              icon={<RefreshCw size={22} />}
              title="Fits your workflow"
              description="Use email approvals for simplicity or the dashboard for full visibility."
            />
          </div>
        </div>
      </section> */}

      {/* Live Workflow */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 rounded-full border border-indigo-100 bg-indigo-50 px-4 py-2 text-sm font-medium text-indigo-700 mb-5">
              <Sparkles size={16} />
              Email + Dashboard Workflow
            </div>

            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
              Approve from email. Manage from the dashboard.
            </h2>

            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
              Review activity, reputation insights, and response approvals all
              work together so you can stay engaged without adding more work to
              your day.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
            {/* Left column */}
            <div className="lg:col-span-2 space-y-6">
              <LandingReputationScore />

              <LandingRecentActivity />
            </div>

            {/* Right column */}
            <div className="lg:col-span-3">
              <LandingReviewQueue />
            </div>
          </div>

          <div className="p-5 mt-6 bg-slate-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p className="font-medium text-slate-700">
                Want a broader view of your reputation?
              </p>

              <p className="text-slate-500">
                Monitor reviews, activity, approvals, and reputation health in
                one place.
              </p>
            </div>

            <a
              href="/overview-demo"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 transition"
            >
              View Dashboard Demo →
            </a>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            {/* Pricing Card */}
            <div className="p-0 lg:p-4 lg:rounded-2xl lg:border lg:border-slate-200 lg:bg-white lg:shadow-sm order-2 lg:order-1">
              <div className="rounded-2xl bg-gradient-to-br from-slate-950 via-blue-950 to-indigo-950 text-white p-6 md:p-8 overflow-hidden relative">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(99,102,241,0.35),transparent_35%)]" />

                <div className="relative">
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-4 py-2 text-sm text-indigo-100 mb-6">
                    <ShieldCheck size={16} />
                    One simple plan
                  </div>

                  <h3 className="text-2xl font-bold tracking-tight">
                    RevUShield
                  </h3>

                  <p className="text-slate-300 mt-2">
                    Review response management for each business location.
                  </p>

                  <div className="mt-8 flex items-end gap-2">
                    <span className="text-5xl font-bold">$79</span>
                    <span className="text-slate-300 mb-2">
                      /mo per location
                    </span>
                  </div>

                  <p className="text-sm text-indigo-100 mt-3">
                    Additional locations are billed at the same monthly rate.
                  </p>

                  <div className="mt-8 space-y-3">
                    {[
                      "Google review syncing",
                      "AI-generated review responses",
                      "Approval workflow",
                      "Email notifications",
                      "Review dashboard",
                      "Tone customization",
                      "Unlimited responses",
                      "Basic analytics",
                    ].map((feature) => (
                      <div key={feature} className="flex items-start gap-3">
                        <CheckCircle
                          size={18}
                          className="text-green-400 mt-0.5 shrink-0"
                        />
                        <span className="text-sm text-slate-100">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>

                  <a
                    href="/register"
                    className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 font-medium text-white hover:bg-indigo-700 transition shadow-lg shadow-indigo-600/20"
                  >
                    Start Free 7-Day Trial
                    <ArrowRight size={18} />
                  </a>
                </div>
              </div>
            </div>

            {/* Copy */}
            <div className="lg:order-2 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 rounded-full bg-indigo-50 text-indigo-700 px-4 py-2 text-sm font-medium mb-5">
                <CreditCard size={16} />
                Simple pricing
              </div>

              <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
                One product. One price. No confusing tiers.
              </h2>

              <p className="text-slate-600 mt-4">
                RevUShield is priced per business location, so it scales
                naturally with how local businesses actually operate. No feature
                matrix, no plan confusion, and no guessing which tier you need.
              </p>

              <ul className="mt-6 space-y-3 text-slate-700">
                {[
                  "One straightforward monthly price",
                  "Every location gets the full RevUShield workflow",
                  "Built for Google Business Profile locations",
                  "Easy to understand for single and multi-location businesses",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2 text-left max-w-md mx-auto lg:mx-0"
                  >
                    <CheckCircle size={18} className="text-green-600 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <p className="text-sm font-semibold text-slate-900">
                  Example pricing
                </p>

                <div className="mt-4 space-y-3 text-sm text-slate-600">
                  <div className="flex items-center justify-between">
                    <span>1-location dentist office</span>
                    <span className="font-semibold text-slate-900">$79/mo</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span>3-location med spa</span>
                    <span className="font-semibold text-slate-900">
                      $237/mo
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span>5-location restaurant group</span>
                    <span className="font-semibold text-slate-900">
                      $395/mo
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 rounded-full bg-indigo-50 text-indigo-700 px-4 py-2 text-sm font-medium mb-5">
              <ShieldCheck size={16} />
              Trusted by growing local businesses
            </div>

            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              Businesses stay responsive without adding more work
            </h2>

            <p className="text-slate-600 mt-4">
              RevUShield helps teams respond faster, stay consistent, and
              maintain a professional presence across every review.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                name: "Megan R.",
                business: "Med Spa Owner",
                image:
                  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=400&auto=format&fit=crop",
                quote:
                  "The responses sound polished and natural. It honestly feels like having someone dedicated to handling our reviews.",
              },
              {
                name: "Chris D.",
                business: "Dental Practice Manager",
                image:
                  "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?q=80&w=400&auto=format&fit=crop",
                quote:
                  "We used to forget reviews constantly during busy weeks. Now every review gets handled quickly and consistently.",
              },
              {
                name: "Lauren S.",
                business: "Home Services Business",
                image:
                  "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=400&auto=format&fit=crop",
                quote:
                  "Approving from email is huge for us. We stay on top of reviews without needing another complicated system.",
              },
            ].map((testimonial) => (
              <div
                key={testimonial.name}
                className="bg-white border border-slate-200 rounded-3xl p-7 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        size={16}
                        className="fill-yellow-400 text-yellow-400"
                      />
                    ))}
                  </div>

                  <div className="rounded-2xl p-2 bg-indigo-50 text-indigo-600">
                    <Quote size={18} />
                  </div>
                </div>

                <p className="text-slate-700 leading-relaxed text-[15px]">
                  “{testimonial.quote}”
                </p>

                {/* pushes footer to bottom */}
                <div className="mt-auto pt-7">
                  <div className="border-t border-slate-200 pt-5 flex items-center gap-4">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="h-12 w-12 rounded-full object-cover"
                    />

                    <div>
                      <p className="font-semibold text-slate-900">
                        {testimonial.name}
                      </p>

                      <p className="text-sm text-slate-500 mt-0.5">
                        {testimonial.business}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
            Frequently Asked Questions
          </h2>

          <p className="text-slate-600 mt-4">
            Answers to common questions about RevUShield.
          </p>
        </div>

        <div className="max-w-4xl mx-auto px-6 space-y-4">
          {faqData.map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition"
              onClick={() => toggleIndex(index)}
            >
              <div className="flex justify-between items-center gap-4">
                <h3 className="text-lg font-semibold text-slate-900 text-left">
                  {item.question}
                </h3>

                {openIndex === index ? (
                  <ChevronUp size={20} className="text-indigo-600 shrink-0" />
                ) : (
                  <ChevronDown size={20} className="text-slate-400 shrink-0" />
                )}
              </div>

              {openIndex === index && (
                <p className="mt-4 text-slate-600 text-left leading-relaxed">
                  {item.answer}
                </p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-slate-950 via-blue-950 to-indigo-950 text-white">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
            Never leave another review unanswered
          </h2>

          <p className="text-lg text-slate-300 mt-5 max-w-2xl mx-auto">
            Stay responsive, protect your reputation, and save time every week.
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
            <a
              href="/register"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 font-medium text-white hover:bg-indigo-700 transition shadow-lg shadow-indigo-600/20"
            >
              Start Free Trial
              <ArrowRight size={18} />
            </a>

            <a
              href="/dashboard-demo"
              className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/10 px-5 py-3 font-medium text-white hover:bg-white/15 transition"
            >
              View Demo
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}

// function FeatureCard({ icon, title, description, highlighted }) {
//   return (
//     <div
//       className={`rounded-2xl border p-6 shadow-sm hover:shadow-md transition ${
//         highlighted
//           ? "bg-indigo-50 border-indigo-200"
//           : "bg-white border-slate-200"
//       }`}
//     >
//       <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-indigo-100 text-indigo-600 mb-5">
//         {icon}
//       </div>

//       <h3 className="text-lg font-semibold mb-2 text-slate-900">{title}</h3>

//       <p className="text-slate-600 leading-relaxed">{description}</p>
//     </div>
//   );
// }

function StepCard({ step, icon, title, description }) {
  return (
    <div className="relative bg-white rounded-2xl border border-slate-200 p-6 text-left shadow-sm hover:shadow-md transition">
      <div className="flex items-center gap-3 mb-5">
        {/* <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-indigo-600 text-white font-bold">
          {step}
        </div> */}

        <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center">
          {icon}
        </div>
      </div>

      <h3 className="text-lg font-semibold text-slate-900 mb-2">{title}</h3>

      <p className="text-slate-600 leading-relaxed">{description}</p>
    </div>
  );
}

function LandingReputationScore() {
  const helping = [
    "High response rate",
    "Positive review momentum",
    "Consistent customer engagement",
  ];

  const hurting = [
    "3 reviews awaiting approval",
    "1 low-rating review needs review",
  ];

  return (
    <div className="bg-white border border-slate-200 rounded-3xl shadow-sm p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Reputation Score</h3>
          <p className="text-sm text-slate-500 mt-1">
            Estimated reputation health
          </p>
        </div>

        <div className="h-10 w-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
          <Star size={19} />
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-end gap-2">
          <p className="text-5xl font-bold text-slate-900">91</p>
          <div className="pb-1">
            <p className="text-sm text-slate-500">/100</p>
            <p className="text-xs font-medium text-green-600 mt-1">
              +6 this month
            </p>
          </div>
        </div>

        <div className="mt-4 h-3 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full w-[91%] bg-indigo-600 rounded-full" />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="h-2 w-2 rounded-full bg-green-500" />
            <h4 className="text-sm font-semibold text-slate-900">
              What’s helping
            </h4>
          </div>

          <div className="space-y-2">
            {helping.map((item) => (
              <div
                key={item}
                className="flex items-start gap-2 text-sm text-slate-600"
              >
                <span className="text-green-600">↑</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="h-2 w-2 rounded-full bg-amber-500" />
            <h4 className="text-sm font-semibold text-slate-900">
              What needs work
            </h4>
          </div>

          <div className="space-y-2">
            {hurting.map((item) => (
              <div
                key={item}
                className="flex items-start gap-2 text-sm text-slate-600"
              >
                <span className="text-amber-600">↓</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function LandingRecentActivity() {
  const activity = [
    {
      icon: Sparkles,
      title: "Response prepared",
      description: "Sarah M. · 5-star review",
      time: "2 min ago",
    },
    {
      icon: AlertCircle,
      title: "Review needs attention",
      description: "3-star review detected",
      time: "18 min ago",
    },
    {
      icon: CheckCircle,
      title: "Response approved",
      description: "Posted to Google",
      time: "1 hr ago",
    },
  ];

  return (
    <div className="bg-white border border-slate-200 rounded-3xl shadow-sm p-5">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Recent Activity</h3>
          <p className="text-sm text-slate-500">What RevUShield has handled</p>
        </div>

        <Sparkles size={20} className="text-indigo-600" />
      </div>

      <div className="space-y-4">
        {activity.map((item) => {
          const Icon = item.icon;

          return (
            <div key={item.title} className="flex gap-3">
              <div className="h-9 w-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                <Icon size={17} />
              </div>

              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {item.title}
                </p>
                <p className="text-sm text-slate-500">{item.description}</p>
                <p className="text-xs text-slate-400 mt-1">{item.time}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LandingReviewQueue() {
  const reviews = [
    {
      initials: "SM",
      name: "Sarah Mitchell",
      rating: 5,
      review:
        "Absolutely love this place! The staff was friendly and everything exceeded my expectations.",
      response:
        "Thank you so much, Sarah! We’re thrilled to hear you had such a great experience with our team.",
      status: "Needs Approval",
      priority: "normal",
    },
    {
      initials: "CP",
      name: "Chris Parker",
      rating: 3,
      review:
        "Decent overall, but communication could have been better. I had to wait longer than expected.",
      response:
        "Thank you for your feedback, Chris. We’re sorry the communication and wait time didn’t meet expectations.",
      status: "Needs Review",
      priority: "high",
    },
    {
      initials: "AL",
      name: "Amanda Lewis",
      rating: 5,
      review:
        "Amazing service. Everyone was helpful and the experience was better than expected.",
      response:
        "Thank you so much, Amanda! We truly appreciate your kind words and support.",
      status: "Ready",
      priority: "normal",
    },
  ];

  return (
    <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden">
      <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Approval Queue</h3>
          <p className="text-sm text-slate-500">
            Reviews waiting for your next action
          </p>
        </div>

        <span className="w-fit rounded-full bg-orange-50 text-orange-700 border border-orange-100 px-3 py-1 text-xs font-semibold">
          3 pending
        </span>
      </div>

      <div className="divide-y divide-slate-100">
        {reviews.map((review) => (
          <div key={review.name} className="p-5">
            <div className="flex flex-col xl:flex-row xl:items-start gap-4">
              <div className="flex gap-3 min-w-0 flex-1">
                <div className="h-10 w-10 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center font-semibold shrink-0">
                  {review.initials}
                </div>

                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h4 className="font-semibold text-slate-900">
                      {review.name}
                    </h4>

                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                        review.priority === "high"
                          ? "bg-red-50 text-red-700"
                          : "bg-orange-50 text-orange-700"
                      }`}
                    >
                      {review.status}
                    </span>
                  </div>

                  <div className="flex gap-0.5 mt-1">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <Star
                        key={index}
                        size={15}
                        className={
                          index < review.rating
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-slate-300"
                        }
                      />
                    ))}
                  </div>

                  <p className="text-sm text-slate-600 leading-6 mt-3">
                    “{review.review}”
                  </p>
                </div>
              </div>

              <div className="xl:w-[45%] rounded-2xl bg-indigo-50 border border-indigo-100 p-4">
                <div className="flex items-center gap-2 text-indigo-700 text-sm font-semibold">
                  <Sparkles size={15} />
                  Prepared Response
                </div>

                <p className="text-sm text-slate-700 leading-6 mt-2">
                  {review.response}
                </p>

                <div className="flex flex-wrap gap-2 mt-4">
                  <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition">
                    <Check size={15} />
                    Approve
                  </button>

                  <button className="border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 px-3 py-2 rounded-xl text-sm font-medium transition">
                    Edit
                  </button>

                  <button className="border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 px-3 py-2 rounded-xl text-sm font-medium transition">
                    Reject
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* <div className="p-5 border-t border-slate-100 bg-slate-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-slate-500">
          Keep every review moving without digging through your inbox.
        </p>

        <a
          href="/overview-demo"
          className="text-sm font-semibold text-indigo-600 hover:text-indigo-700"
        >
          View dashboard →
        </a>
      </div> */}
    </div>
  );
}
